// Gene tables (HER-2, ARCH §28.3). PURE (ENG-1).
//
// The structural shape of a table is zod's (`registry/schemas.ts` `geneSchema`); the
// rules that span fields — or reach into the rest of the def — are here, and run at
// registry load so a bad table is a load error (`pnpm audit:registry` red), never a
// genome that decodes wrong in the middle of a world.
import { HER } from '../config';
import type { Gene, MendelianGene, PolygenicGene } from '../registry/schemas';

export type { Gene, MendelianGene, PolygenicGene };

/** Every scalar a polygenic gene may set (§28.6 animals, §28.10 crops). A name outside
 *  this set would express into nothing, so it is refused at load. */
export const SCALAR_TARGETS: ReadonlySet<string> = new Set([
  // animals
  'speedMul', 'eggMul', 'scale', 'fleeMul', 'wanderMul', 'cohesion', 'startleP',
  'lifespanTicks', 'jumpMul', 'healthMul', 'regrowMul',
  // crops
  'growthMul', 'yieldMul', 'seedBonus', 'droughtTol', 'stalkQ',
]);

/** Σ freq must be 1 within this — a table is hand-typed JSON, not arithmetic. */
const FREQ_EPSILON = 1e-6;

/** Check a gene table against §28.3's load-time rules. Returns the FIRST violation as a
 *  message naming the gene, or null. `rigParts` is the def's rig part names — a tint or
 *  part naming a box the rig does not have would paint nothing. */
export function validateGenes(genes: readonly Gene[], rigParts: readonly string[]): string | null {
  if (genes.length > HER.GENES_MAX) return `a gene table may have at most ${HER.GENES_MAX} genes (has ${genes.length})`;
  const seen = new Set<string>();
  const rig = new Set(rigParts);
  for (const g of genes) {
    if (seen.has(g.name)) return `gene '${g.name}' is declared twice`;
    seen.add(g.name);
    const bad = g.kind === 'mendelian' ? checkMendelian(g, rig) : checkPolygenic(g);
    if (bad) return `gene '${g.name}': ${bad}`;
  }
  return null;
}

function checkMendelian(g: MendelianGene, rig: ReadonlySet<string>): string | null {
  if (new Set(g.alleles).size !== g.alleles.length) return 'alleles must be distinct';
  if (!g.alleles.includes(g.dominant)) return `dominant '${g.dominant}' is not one of its alleles (${g.alleles.join(', ')})`;
  if (g.freq.length !== g.alleles.length) return `freq has ${g.freq.length} entries for ${g.alleles.length} alleles`;
  const sum = g.freq.reduce((a, b) => a + b, 0);
  if (Math.abs(sum - 1) > FREQ_EPSILON) return `freq sums to ${sum}, not 1`;
  const { tint, part } = g.expresses;
  if (tint) {
    for (const p of tint.parts) if (!rig.has(p)) return `tint names rig part '${p}', which this mob's rig does not have`;
  }
  if (part && !rig.has(part.name)) return `part names rig part '${part.name}', which this mob's rig does not have`;
  return null;
}

function checkPolygenic(g: PolygenicGene): string | null {
  const { scalar, range } = g.expresses;
  if (!SCALAR_TARGETS.has(scalar)) return `'${scalar}' is not a scalar a gene can express`;
  if (!Number.isFinite(range[0]) || !Number.isFinite(range[1])) return 'range must be finite';
  return null;
}
